import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MonthCalendar, CalendarLegend } from "@/components/MonthCalendar";
import { useOccupancy, useTrips } from "@/hooks/queries";
import { formatDate, startOfMonth, todayISO } from "@/lib/format";

const stateFor = (o, partial) => {
  if (!o) return "available";
  if (o.status === "diblokir") return "blocked";
  return partial ? "partial" : "booked";
};

const SelectedDay = ({ iso, entry, state, trips, tripId }) => {
  if (!iso) {
    return <p className="text-sm text-neutral-500">Klik salah satu tanggal untuk melihat detail dan memesan.</p>;
  }
  const bookable = state === "available" || state === "partial";
  const list = tripId === "all" ? trips.filter((t) => !(entry?.trips || []).includes(t.name)) : trips.filter((t) => t.id === tripId);
  return (
    <div data-testid="jadwal-selected-panel">
      <p className="font-display text-2xl capitalize">{formatDate(iso, { weekday: "long" })}</p>
      <p className={`mt-2 text-sm ${bookable ? "text-emerald-300" : "text-rose-300"}`} data-testid="jadwal-selected-status">
        {state === "available" && "Tersedia untuk dipesan"}
        {state === "partial" && "Sebagian trip sudah terisi"}
        {state === "booked" && "Sudah terisi — silakan pilih tanggal lain"}
        {state === "blocked" && "Tanggal ditutup"}
      </p>
      {entry?.trips?.length > 0 && <p className="mt-2 text-xs text-neutral-500">Terisi: {entry.trips.join(", ")}</p>}
      {bookable && (
        <div className="mt-6 flex flex-wrap gap-2">
          {list.map((t) => (
            <Link key={t.id} to={`/trip/${t.id}?tanggal=${iso}`} className="rounded-full border border-white/15 px-4 py-2 text-sm transition-colors hover:border-gold hover:text-gold" data-testid={`jadwal-book-trip-${t.id}`}>
              {t.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const OccupiedList = ({ occ, month }) => {
  const prefix = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
  const days = Object.keys(occ).filter((d) => d.startsWith(prefix)).sort();
  return (
    <div data-testid="jadwal-occupied-list">
      <p className="overline-label">Tanggal terisi bulan ini</p>
      {days.length === 0 && <p className="mt-4 text-sm text-neutral-500">Semua tanggal bulan ini masih tersedia.</p>}
      <ul className="mt-4 divide-y divide-white/5">
        {days.map((d) => (
          <li key={d} className="flex items-start justify-between gap-4 py-3 text-sm">
            <span className="font-mono text-neutral-300">{formatDate(d, { month: "short" })}</span>
            <span className="text-right text-neutral-500">{occ[d].status === "diblokir" ? "Ditutup" : occ[d].trips.join(", ")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function Jadwal() {
  const { data: trips = [] } = useTrips();
  const [tripId, setTripId] = useState("all");
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState(null);
  const { data } = useOccupancy(tripId === "all" ? null : tripId, month);
  const occ = data?.occupied || {};
  const partial = tripId === "all" && data?.mode === "per_trip";
  const today = todayISO();
  const getDay = (iso) =>
    iso < today ? { state: "past", disabled: true } : { state: stateFor(occ[iso], partial), selected: iso === selected };

  return (
    <section className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8 md:pt-40" data-testid="jadwal-page">
      <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <div>
          <p className="overline-label">Jadwal</p>
          <h1 className="mt-4 font-display text-4xl font-light sm:text-5xl lg:text-6xl">Kalender ketersediaan</h1>
          <p className="mt-4 max-w-xl text-neutral-400">Lihat tanggal yang sudah dibooking dan yang masih kosong. Klik tanggal untuk langsung memesan.</p>
        </div>
        <Select value={tripId} onValueChange={(v) => { setTripId(v); setSelected(null); }}>
          <SelectTrigger className="h-12 w-full rounded-full border-white/15 bg-transparent px-5 md:w-72" data-testid="jadwal-trip-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="jadwal-trip-filter-all">Semua trip</SelectItem>
            {trips.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`jadwal-trip-filter-${t.id}`}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="mt-12 grid gap-8 lg:grid-cols-12">
        <div className="panel p-5 sm:p-8 lg:col-span-7">
          <MonthCalendar month={month} onMonthChange={(m) => { setMonth(m); setSelected(null); }} getDay={getDay} onSelect={setSelected} minMonth={new Date()} testId="jadwal-calendar" size="lg" />
          <CalendarLegend items={partial ? ["available", "partial", "blocked"] : ["available", "booked", "blocked"]} />
        </div>
        <div className="space-y-8 lg:col-span-5">
          <div className="panel p-6 sm:p-8">
            <CalendarDays className="mb-4 h-5 w-5 text-gold" />
            <SelectedDay iso={selected} entry={occ[selected]} state={selected ? stateFor(occ[selected], partial) : null} trips={trips} tripId={tripId} />
          </div>
          <div className="panel p-6 sm:p-8">
            <OccupiedList occ={occ} month={month} />
          </div>
        </div>
      </div>
    </section>
  );
}
